import React, { useEffect, useMemo, useState } from 'react';
import styles from './OpenpaySubscriptionConfig.module.css';

const API = process.env.REACT_APP_API_URL || '/api';
const DISCOUNT_PERCENTAGE = 65;

const DEFAULT_PLAN = {
  name: 'Preferente',
  statusAfterRetry: 'cancelled',
  retryTimes: 2,
  repeatUnit: 'month',
  trialDays: 0,
  repeatEvery: 1,
};

function authHeaders() {
  const token = localStorage.getItem('odm_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export function parseTuitionAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const cleaned = String(value || '').replace(/[^\d,.-]/g, '');
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const separatorIndex = Math.max(lastComma, lastDot);
  const decimalLength = separatorIndex >= 0 ? cleaned.length - separatorIndex - 1 : 0;
  const hasDecimalSeparator = decimalLength > 0 && decimalLength <= 2;
  const integerPart = hasDecimalSeparator ? cleaned.slice(0, separatorIndex) : cleaned;
  const decimalPart = hasDecimalSeparator ? cleaned.slice(separatorIndex + 1) : '';
  const normalized = `${integerPart.replace(/[.,]/g, '')}${decimalPart ? `.${decimalPart}` : ''}`;
  const amount = Number(normalized);

  return Number.isFinite(amount) ? amount : 0;
}

function planFormFromOpenpayData(openpayData = {}) {
  return {
    name: openpayData.name || DEFAULT_PLAN.name,
    statusAfterRetry: openpayData.status_after_retry || DEFAULT_PLAN.statusAfterRetry,
    retryTimes: openpayData.retry_times ?? DEFAULT_PLAN.retryTimes,
    repeatUnit: openpayData.repeat_unit || DEFAULT_PLAN.repeatUnit,
    trialDays: openpayData.trial_days ?? DEFAULT_PLAN.trialDays,
    repeatEvery: openpayData.repeat_every ?? DEFAULT_PLAN.repeatEvery,
  };
}

export default function OpenpaySubscriptionConfig({
  educationalProgramType,
  educationalProgramId,
  tuition,
}) {
  const [plan, setPlan] = useState(DEFAULT_PLAN);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const tuitionAmount = useMemo(() => parseTuitionAmount(tuition), [tuition]);
  const subscriptionAmount = useMemo(
    () => Number((tuitionAmount * ((100 - DISCOUNT_PERCENTAGE) / 100)).toFixed(2)),
    [tuitionAmount]
  );

  useEffect(() => {
    setPlan(DEFAULT_PLAN);
    setCurrentPlan(null);
    setError('');
    setSuccess('');

    if (!educationalProgramId) return undefined;

    const controller = new AbortController();
    const query = new URLSearchParams({
      educationalProgramType,
      educationalProgramId,
      inUse: 'true',
    });

    setLoading(true);
    fetch(`${API}/subscription-plans?${query.toString()}`, {
      headers: authHeaders(),
      signal: controller.signal,
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.message || 'No se pudo consultar el plan actual.');
        return body;
      })
      .then(({ data }) => {
        const activePlan = Array.isArray(data) ? data[0] : null;
        setCurrentPlan(activePlan || null);
        if (activePlan?.openpayData) setPlan(planFormFromOpenpayData(activePlan.openpayData));
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [educationalProgramId, educationalProgramType]);

  const updatePlan = (field, value) => {
    setPlan((previous) => ({ ...previous, [field]: value }));
    setError('');
    setSuccess('');
  };

  const saveSubscriptionPlan = async () => {
    setError('');
    setSuccess('');

    if (!educationalProgramId) {
      setError('Guarda primero el programa educativo para poder crear su plan de Openpay.');
      return;
    }
    if (subscriptionAmount <= 0) {
      setError('Captura una colegiatura válida mayor a cero.');
      return;
    }
    if (!plan.name.trim()) {
      setError('El nombre del plan es obligatorio.');
      return;
    }

    const retryTimes = Number(plan.retryTimes);
    const trialDays = Number(plan.trialDays);
    const repeatEvery = Number(plan.repeatEvery);
    if (
      !Number.isInteger(retryTimes)
      || !Number.isInteger(trialDays)
      || !Number.isInteger(repeatEvery)
      || retryTimes < 0
      || trialDays < 0
      || repeatEvery < 1
    ) {
      setError('Reintentos, días de prueba y frecuencia deben ser números enteros válidos.');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/subscription-plans`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          educationalProgramType,
          educationalProgramId,
          plan: {
            amount: subscriptionAmount,
            status_after_retry: plan.statusAfterRetry,
            retry_times: retryTimes,
            name: plan.name.trim(),
            repeat_unit: plan.repeatUnit,
            trial_days: trialDays,
            repeat_every: repeatEvery,
            currency: 'MXN',
          },
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudo crear el plan de Openpay.');

      setCurrentPlan(body.data || null);
      if (body.data?.openpayData) setPlan(planFormFromOpenpayData(body.data.openpayData));
      setSuccess('El nuevo plan se creó y quedó marcado como plan en uso.');
    } catch (requestError) {
      setError(requestError.message || 'No se pudo conectar con el servidor.');
    } finally {
      setSaving(false);
    }
  };

  const fieldsDisabled = !educationalProgramId || loading || saving;

  return (
    <section className={styles.container} aria-labelledby={`openpay-title-${educationalProgramType}`}>
      <div className={styles.header}>
        <div>
          <h2 id={`openpay-title-${educationalProgramType}`} className={styles.title}>
            Configuración Openpay
          </h2>
          <p className={styles.description}>
            Cada guardado crea un plan nuevo y lo establece como el plan preferente en uso.
          </p>
        </div>
        <span className={currentPlan ? styles.activeBadge : styles.inactiveBadge}>
          {loading
            ? 'Consultando plan...'
            : currentPlan?.openpayData?.id
              ? `En uso: ${currentPlan.openpayData.id}`
              : 'Sin plan en uso'}
        </span>
      </div>

      {!educationalProgramId && (
        <p className={styles.notice}>Guarda primero el programa y vuelve a editarlo para configurar Openpay.</p>
      )}

      <div className={styles.grid}>
        <label className={styles.label}>
          Nombre del plan
          <input
            className={styles.input}
            value={plan.name}
            onChange={(event) => updatePlan('name', event.target.value)}
            disabled={fieldsDisabled}
          />
        </label>

        <label className={styles.label}>
          Estado después de reintentos
          <select
            className={styles.input}
            value={plan.statusAfterRetry}
            onChange={(event) => updatePlan('statusAfterRetry', event.target.value)}
            disabled={fieldsDisabled}
          >
            <option value="cancelled">Cancelar suscripción</option>
            <option value="unpaid">Marcar como no pagada</option>
          </select>
        </label>

        <label className={styles.label}>
          Número de reintentos
          <input
            className={styles.input}
            type="number"
            min="0"
            step="1"
            value={plan.retryTimes}
            onChange={(event) => updatePlan('retryTimes', event.target.value)}
            disabled={fieldsDisabled}
          />
        </label>

        <label className={styles.label}>
          Unidad de repetición
          <select
            className={styles.input}
            value={plan.repeatUnit}
            onChange={(event) => updatePlan('repeatUnit', event.target.value)}
            disabled={fieldsDisabled}
          >
            <option value="week">Semana</option>
            <option value="month">Mes</option>
            <option value="year">Año</option>
          </select>
        </label>

        <label className={styles.label}>
          Días de prueba
          <input
            className={styles.input}
            type="number"
            min="0"
            step="1"
            value={plan.trialDays}
            onChange={(event) => updatePlan('trialDays', event.target.value)}
            disabled={fieldsDisabled}
          />
        </label>

        <label className={styles.label}>
          Frecuencia de cobro
          <input
            className={styles.input}
            type="number"
            min="1"
            step="1"
            value={plan.repeatEvery}
            onChange={(event) => updatePlan('repeatEvery', event.target.value)}
            disabled={fieldsDisabled}
          />
        </label>

        <label className={styles.label}>
          Colegiatura
          <input className={styles.readonlyInput} type="number" value={tuitionAmount || ''} readOnly />
        </label>

        <label className={styles.label}>
          Descuento aplicado
          <input className={styles.readonlyInput} value={`${DISCOUNT_PERCENTAGE}%`} readOnly />
        </label>

        <label className={styles.label}>
          Monto recurrente
          <input className={styles.readonlyInput} type="number" value={subscriptionAmount || ''} readOnly />
        </label>

        <label className={styles.label}>
          Moneda
          <input className={styles.readonlyInput} value="MXN" disabled />
        </label>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.saveButton}
          onClick={saveSubscriptionPlan}
          disabled={fieldsDisabled || subscriptionAmount <= 0}
        >
          {saving ? 'Creando plan...' : 'Guardar configuración Openpay'}
        </button>
      </div>
    </section>
  );
}
